import { useCallback, useState, type ReactNode } from 'react';
import { Button, Caption1, Field, Input, MessageBar, MessageBarBody, Select, Text, makeStyles, mergeClasses } from '@fluentui/react-components';
import { tokens } from '@vigeo/ui';
import { writableOfferTermsSchema, offerEmploymentTypes, servicePlacesWords, offerFilingCategorySchema, fileCategoryLabels, type HrWorkflowSettings, type OfferEmploymentType, type OfferTerms, type Person } from '@vigeo/shared';
import { HrFileUpload, type HrUpload } from '@/features/uploads';
import { PlacePicker } from '@/features/places';

/**
 * 1.2 · Draft the offer — frame 06b `294:567`.
 *
 * The frame's card is four labelled rows (Position, Dates, Compensation, Offer letter) over a
 * footer, each row a 200px label column beside its fields. That shape is the build, not a
 * decoration: the label column is where the frame puts the sentence that explains the row, and
 * the rows are what separate the offered facts from the letter they came from.
 *
 * WHAT THE FRAME REPLACED. The offer used to require HR to retype the approved wording into a
 * 16,000-character textarea. The frame has no such field: HR enters the facts and uploads the
 * approved PDF (#260). The textarea and the "reusable offer wording" template picker are both
 * gone from this screen for that reason. `termsText` stays optional in the schema so drafts
 * written before the change keep parsing and keep their wording on save.
 */

const useStyles = makeStyles({
  card: {
    display: 'flex', flexDirection: 'column', minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
  },
  intro: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS,
    paddingInline: tokens.spacingHorizontalXL, paddingBlock: tokens.spacingVerticalL,
  },
  crumb: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalXXS, flexWrap: 'wrap' },
  crumbLead: { color: tokens.colorBrandForeground1 },
  crumbHere: { fontWeight: tokens.fontWeightSemibold },
  title: {
    margin: 0, fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600,
    fontWeight: tokens.fontWeightSemibold,
  },
  lede: { color: tokens.colorNeutralForeground2 },

  row: {
    display: 'flex', flexDirection: 'row', alignItems: 'flex-start', flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXXL, padding: tokens.spacingHorizontalXL,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  labelColumn: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS,
    width: '12.5rem', flexGrow: 0, flexShrink: 0,
  },
  labelTitle: { fontWeight: tokens.fontWeightSemibold },
  labelHelp: { color: tokens.colorNeutralForeground2 },
  fields: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, flexGrow: 1, flexBasis: '20rem', minWidth: 0 },
  line: { display: 'flex', flexDirection: 'row', alignItems: 'flex-start', flexWrap: 'wrap', gap: tokens.spacingHorizontalM },
  field: { display: 'flex', flexDirection: 'column', flexGrow: 1, flexBasis: '10rem', minWidth: 0 },
  /** Amount and Per are fixed narrow columns in the frame; Description takes the rest. */
  narrow: { flexGrow: 0, flexBasis: '7.5rem', maxWidth: '7.5rem' },
  /** The frame's third column on a two-field line, so the pair does not stretch to full width. */
  spacer: { flexGrow: 1, flexBasis: '10rem', minWidth: 0 },

  rate: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalM, minWidth: 0,
  },
  rateTop: { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalS },
  rateLabel: { color: tokens.colorNeutralForeground2, fontWeight: tokens.fontWeightSemibold },
  inlineAction: { alignSelf: 'flex-start' },

  letter: {
    display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    paddingInline: tokens.spacingHorizontalM, paddingBlock: tokens.spacingVerticalS,
    minWidth: 0,
  },
  letterText: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, flexGrow: 1, minWidth: 0 },
  letterName: { fontWeight: tokens.fontWeightSemibold, overflowWrap: 'anywhere' },
  letterMeta: { color: tokens.colorNeutralForeground2 },

  footer: {
    display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderBottomLeftRadius: tokens.borderRadiusLarge, borderBottomRightRadius: tokens.borderRadiusLarge,
    paddingInline: tokens.spacingHorizontalXL, paddingBlock: tokens.spacingVerticalL,
  },
  footerNote: { flexGrow: 1, minWidth: 0, color: tokens.colorNeutralForeground2 },
});

/** The frame labels this field "Offer letter (PDF)". The server allowlist is the wider one. */
const OFFER_LETTER_TYPES = ['application/pdf'] as const;

/**
 * WHAT THE LETTER FIELD IS IN — which is NOT the same question as "is there an upload object".
 *
 * `HrFileUpload` announces `null` as the FIRST thing it does on every file pick, and announces an
 * upload only once staging resolves. So `null` means "HR chose a file and nothing has come back for
 * it" — mid-upload, or a 413, a 415 or a dropped connection. (It does NOT mean the malware scan has
 * cleared: `HrUpload.usable` says that, and nothing on this screen has ever consulted it. Saving a
 * draft whose letter is still being scanned is the existing, deliberate behaviour — the control
 * itself says "you can save once it is clean" — and `issue-offer` is where the server enforces it.)
 *
 * Holding this as `HrUpload | null` made that state indistinguishable from "HR never touched the
 * field", and the two need opposite treatment: untouched keeps whatever the draft already carries,
 * while a failed pick must keep NOTHING. Conflating them meant a failed replacement fell back to
 * `terms.letterUploadId` — the OLD letter — and saved successfully, so the offer went on resting on
 * the letter HR believed they had just replaced, with no sign that anything had gone wrong (#302
 * post-merge review).
 */
type LetterChoice =
  /** Untouched. Whatever the draft already carries stays, including nothing at all. */
  | { readonly kind: 'kept' }
  /** A file was picked and nothing has come back for it. NEVER falls back to the kept letter. */
  | { readonly kind: 'unresolved' }
  | { readonly kind: 'replaced'; readonly upload: HrUpload };

/** What the draft currently rests on, when HR has not (yet) replaced it. */
const keptLetter = {
  name: 'An offer letter is already attached',
  meta: 'Uploaded with this draft. Choose a file above to replace it, or leave the field empty to keep it.',
  /**
   * The same letter, said differently, because the field is NOT empty any more — a file was chosen
   * and did not land. Telling HR to "leave the field empty" here would contradict the refusal shown
   * beside it.
   */
  metaUnresolved: 'The file you chose has not been attached, so this is still what the offer rests on.',
  /** The way back to `kept`, which HR has to be able to take without discarding their other edits. */
  keepAction: 'Keep the attached letter',
} as const;

function unresolvedLetterMessage(hasKeptLetter: boolean): string {
  return hasKeptLetter
    ? `That file has not finished uploading, so nothing was saved. The offer still rests on the letter already attached — wait for the upload to finish, try it again, or choose “${keptLetter.keepAction}”.`
    : 'That file has not finished uploading, so nothing was saved. Wait for the upload to finish, or try it again.';
}

/** The rate unit each employment type is paid in. Mileage is a separate reimbursement line (`unit: 'mile'`) on any of them. */
const unitFor: Record<OfferEmploymentType, OfferTerms['compensation'][number]['unit']> = { Hourly: 'hour', Salary: 'year', PRN: 'visit' };

/** The Select's value: one of the three, or '' for anything else (an old free-text value), so HR must choose. */
const employmentTypeChoice = (value: string): OfferEmploymentType | '' =>
  (offerEmploymentTypes as readonly string[]).includes(value) ? (value as OfferEmploymentType) : '';

/** Either terms the API will accept, or the sentence HR is shown instead of a save. */
type SaveAttempt =
  | { readonly ok: true; readonly terms: OfferTerms }
  | { readonly ok: false; readonly message: string };

const localTime = (value: string) => { const d = new Date(value); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };

/** Kilobytes, the unit the frame's attached-file line uses. */
function fileSize(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function LabelledRow({ label, help, children }: { readonly label: string; readonly help: string; readonly children: ReactNode }) {
  const styles = useStyles();
  return <div className={styles.row}>
    <div className={styles.labelColumn}>
      <Text className={styles.labelTitle}>{label}</Text>
      <Caption1 className={styles.labelHelp}>{help}</Caption1>
    </div>
    <div className={styles.fields}>{children}</div>
  </div>;
}

export function OfferEditor({ person, initial, settings, busy, onSave, onCancel, returnsToDraft = false }: {
  readonly person: Person; readonly initial?: OfferTerms; readonly busy: boolean;
  /** Editing a reviewed offer: saving takes it back to draft, and it has to be reviewed again. */
  readonly returnsToDraft?: boolean;
  readonly settings: HrWorkflowSettings;
  readonly onSave: (terms: OfferTerms) => Promise<void>; readonly onCancel: () => void;
}) {
  const styles = useStyles();
  const [terms, setTerms] = useState<OfferTerms>(initial ?? {
    target: settings.defaultOfferTarget,
    position: '', department: '', employmentClassification: 'w2', employmentType: '', serviceArea: '',
    proposedStartDate: '', acceptanceDeadline: '', compensation: [{ description: '', amount: 0, unit: 'hour', details: '' }],
  });
  const [deadline, setDeadline] = useState(initial ? localTime(initial.acceptanceDeadline) : localTime(new Date(Date.now() + settings.defaultOfferResponseDays * 86400000).toISOString()));
  const [letter, setLetter] = useState<LetterChoice>({ kind: 'kept' });
  const [error, setError] = useState('');
  const set = <K extends keyof OfferTerms>(key: K, value: OfferTerms[K]) => { setTerms((t) => ({ ...t, [key]: value })); };
  const setRate = (index: number, change: Partial<OfferTerms['compensation'][number]>) =>
    set('compensation', terms.compensation.map((r, i) => i === index ? { ...r, ...change } : r));
  /**
   * Choosing a type re-points every non-mileage rate at the unit that type is paid in. A rate whose
   * unit CHANGES is cleared to 0 as well: 31.50 an hour is not 31.50 a year, and keeping the number
   * would let it be saved by mistake. HR enters the new rate. Mileage and a rate already in the
   * right unit are left alone.
   */
  const changeEmploymentType = (value: string) => setTerms((t) => {
    const type = employmentTypeChoice(value);
    const repoint = (r: OfferTerms['compensation'][number]) => (r.unit === 'mile' || type === '' || r.unit === unitFor[type] ? r : { ...r, unit: unitFor[type], amount: 0 });
    return { ...t, employmentType: value, compensation: t.compensation.map(repoint) };
  });
  const chosenType = employmentTypeChoice(terms.employmentType);
  const addRate = (unit: OfferTerms['compensation'][number]['unit'], description = '') =>
    set('compensation', [...terms.compensation, { description, amount: 0, unit, details: '' }]);
  const keptLetterId = terms.letterUploadId;
  /**
   * Stable, and deliberately so. `HrFileUpload` omits `onChange` from its effect deps behind an
   * `eslint-disable` reading "re-running on its identity would loop" — which an inline arrow here
   * would turn from a comment into a live footgun. It also clears a stale refusal: once the letter
   * state moves, the sentence explaining the last refusal is no longer about what is on screen.
   */
  const onLetterChange = useCallback((upload: HrUpload | null) => {
    setError('');
    setLetter(upload === null ? { kind: 'unresolved' } : { kind: 'replaced', upload });
  }, []);
  /**
   * The ONLY route to `onSave`. `unresolved` is refused here rather than being handed to the schema,
   * because the schema cannot tell a letter HR meant to keep from one they meant to replace — and
   * the fall-through it would take (`...terms`) is precisely the old letter this must not reuse.
   */
  /** The attached-file line the frame draws: the replacement if one landed, else the kept letter. */
  const letterSummary = letter.kind === 'replaced'
    ? { name: letter.upload.fileName, meta: `PDF · ${fileSize(letter.upload.byteLength)}` }
    : keptLetterId === undefined ? null
      : { name: keptLetter.name, meta: letter.kind === 'unresolved' ? keptLetter.metaUnresolved : keptLetter.meta };
  const attemptSave = (): SaveAttempt => {
    if (letter.kind === 'unresolved') return { ok: false, message: unresolvedLetterMessage(keptLetterId !== undefined) };
    if ((terms.servicePlaces ?? []).length === 0) return { ok: false, message: 'Pick at least one service area.' };
    const result = writableOfferTermsSchema.safeParse({
      ...terms,
      acceptanceDeadline: deadline ? new Date(deadline).toISOString() : '',
      ...(letter.kind === 'replaced' ? { letterUploadId: letter.upload.uploadId } : {}),
    });
    return result.success
      ? { ok: true, terms: result.data }
      : { ok: false, message: result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ') };
  };
  const given = person.name.givenName;

  return <section className={styles.card} aria-labelledby="draft-offer-heading">
    <div className={styles.intro}>
      <div className={styles.crumb}>
        <Caption1 className={styles.crumbLead}>Offer and stage</Caption1>
        <Caption1 aria-hidden="true">›</Caption1>
        <Caption1 className={styles.crumbHere}>Draft the offer</Caption1>
      </div>
      <h3 id="draft-offer-heading" className={styles.title}>Draft the offer</h3>
      <Text className={styles.lede}>
        Enter the details from the approved offer letter, then upload the letter itself so it can be signed.
        You can change a draft until it’s marked reviewed.
      </Text>
    </div>

    {returnsToDraft ? <div className={styles.row}><MessageBar intent="warning"><MessageBarBody>This offer has been reviewed. Saving your changes returns it to draft, and you will need to review it again before it can be sent.</MessageBarBody></MessageBar></div> : null}

    <LabelledRow label="Position" help="Employment type is Hourly, Salary or PRN.">
      <div className={styles.line}>
        <Field className={styles.field} label="Position" required><Input value={terms.position} onChange={(_, d) => set('position', d.value)} /></Field>
        <Field className={styles.field} label="Department" required><Input value={terms.department} onChange={(_, d) => set('department', d.value)} /></Field>
        <div className={styles.spacer} />
      </div>
      <div className={styles.line}>
        <Field className={styles.field} label="Classification" required>
          <Select value={terms.employmentClassification} onChange={(_, d) => set('employmentClassification', d.value as OfferTerms['employmentClassification'])}>
            <option value="w2">W-2 employee</option><option value="1099">1099 contractor</option>
          </Select>
        </Field>
        <Field className={styles.field} label="Employment type" required>
          {/* A value that is not one of the three (an old draft's "Full-time", or terms copied from an offer signed before #348) shows as unselected, so it has to be chosen again. */}
          <Select value={employmentTypeChoice(terms.employmentType)} onChange={(_, d) => changeEmploymentType(d.value)}>
            <option value="">Choose…</option>
            {offerEmploymentTypes.map((type) => <option key={type} value={type}>{type}</option>)}
          </Select>
        </Field>
        <div className={styles.spacer} />
      </div>
      <div className={styles.line}>
        <div className={styles.field}>
          {/* The same picker the Roster uses (#764), so the area carries over to the employee record when the hire is made Active. */}
          <PlacePicker value={terms.servicePlaces ?? []} onChange={(places) => setTerms((t) => ({ ...t, servicePlaces: [...places], serviceArea: servicePlacesWords(places) }))} />
          {(terms.servicePlaces ?? []).length === 0 && terms.serviceArea.trim() !== '' ? (
            <Caption1>This offer was written with a typed service area: “{terms.serviceArea.trim()}”. Pick the places above to replace it.</Caption1>
          ) : null}
        </div>
      </div>
    </LabelledRow>

    <LabelledRow label="Dates" help={`Accept by is the last moment ${given} can sign in the portal.`}>
      <div className={styles.line}>
        <Field className={styles.field} label="Proposed start date" required><Input type="date" value={terms.proposedStartDate} onChange={(_, d) => set('proposedStartDate', d.value)} /></Field>
        <Field className={styles.field} label="Accept by" required hint={`${settings.defaultOfferResponseDays} days from today, the agency default`}>
          <Input type="datetime-local" value={deadline} onChange={(_, d) => setDeadline(d.value)} />
        </Field>
      </div>
    </LabelledRow>

    <LabelledRow label="Compensation" help="Rates only, for reference on the record.">
      {terms.compensation.map((rate, index) => <div key={index} className={styles.rate}>
        <div className={styles.rateTop}>
          <Caption1 className={styles.rateLabel}>Rate {index + 1}</Caption1>
          {terms.compensation.length > 1
            ? <Button appearance="transparent" size="small" disabled={busy} onClick={() => set('compensation', terms.compensation.filter((_, i) => i !== index))}>Remove rate {index + 1}</Button>
            : null}
        </div>
        <div className={styles.line}>
          <Field className={styles.field} label="Description" required><Input value={rate.description} onChange={(_, d) => setRate(index, { description: d.value })} /></Field>
          <Field className={mergeClasses(styles.field, styles.narrow)} label="Amount" required>
            <Input type="number" min={0} step="0.01" value={String(rate.amount)} onChange={(_, d) => setRate(index, { amount: Number(d.value) })} />
          </Field>
          <Field className={mergeClasses(styles.field, styles.narrow)} label="Per" required>
            <Select value={rate.unit} onChange={(_, d) => setRate(index, { unit: d.value as typeof rate.unit })}>
              {(chosenType === '' ? ['hour', 'visit', 'year', 'day', 'mile', 'other'] : [unitFor[chosenType], 'mile']).map((unit) => <option key={unit} value={unit}>{unit}</option>)}
            </Select>
          </Field>
        </div>
      </div>)}
      {chosenType === '' ? <Button className={styles.inlineAction} appearance="transparent" disabled={busy || terms.compensation.length >= 25}
        onClick={() => addRate('visit')}>Add a compensation row</Button> : <>
        {/* Hourly and Salary carry one rate; PRN carries a per-visit rate for each discipline. Mileage is its own line on any of them. */}
        {chosenType === 'PRN' ? <Button className={styles.inlineAction} appearance="transparent" disabled={busy || terms.compensation.length >= 25}
          onClick={() => addRate('visit')}>Add a per-visit rate</Button> : null}
        <Button className={styles.inlineAction} appearance="transparent" disabled={busy || terms.compensation.length >= 25}
          onClick={() => addRate('mile', 'Mileage')}>Add mileage reimbursement</Button>
      </>}
    </LabelledRow>

    <LabelledRow label="Offer letter" help={`Upload the final PDF last. It is recorded against this offer for ${given} to sign.`}>
      <HrFileUpload label="Offer letter (PDF)" required target={{ kind: 'evidence', personId: person.id }}
        acceptedTypes={OFFER_LETTER_TYPES} disabled={busy} onChange={onLetterChange} />
      {/*
        THIS LINE IS WHY A FAILED REPLACEMENT USED TO BE SILENT. It read `letter !== null`, which
        only a fresh upload ever satisfied, so re-opening a saved draft showed an empty required file
        input and NO sign of the letter the offer already rested on. Whatever HR did next, they were
        working blind. A kept letter is now stated — by description, since the draft carries only its
        upload id and inventing a name or size here would be worse than saying neither.
      */}
      {letterSummary === null ? null : <div className={styles.letter}>
        <div className={styles.letterText}>
          <Text className={styles.letterName}>{letterSummary.name}</Text>
          <Caption1 className={styles.letterMeta}>{letterSummary.meta}</Caption1>
        </div>
      </div>}
      {/*
        THE WAY BACK. Without it `unresolved` is a one-way door: a 413 on a large PDF would leave HR
        with a Save that refuses and a Cancel that throws away every other field they had typed.
        This is an explicit decision to keep the attached letter, which is the one thing the silent
        fall-through never was.
      */}
      {letter.kind === 'unresolved' && keptLetterId !== undefined
        ? <Button className={styles.inlineAction} appearance="transparent" disabled={busy}
            onClick={() => { setLetter({ kind: 'kept' }); setError(''); }}>{keptLetter.keepAction}</Button>
        : null}
      <div className={styles.line}>
        <Field className={styles.field} label="File the signed copy to" required>
          <Select value={terms.target} onChange={(_, d) => set('target', offerFilingCategorySchema.parse(d.value))}>
            {offerFilingCategorySchema.options.map((target) => <option key={target} value={target}>{fileCategoryLabels[target]}</option>)}
          </Select>
        </Field>
        <div className={styles.spacer} />
      </div>
    </LabelledRow>

    {error ? <div className={styles.row}><Text role="alert">{error}</Text></div> : null}

    <div className={styles.footer}>
      <Caption1 className={styles.footerNote}>Saved on the audit trail.</Caption1>
      <Button disabled={busy} onClick={onCancel}>Cancel</Button>
      <Button appearance="primary" disabled={busy} onClick={() => {
        const attempt = attemptSave();
        if (!attempt.ok) { setError(attempt.message); return; }
        setError('');
        void onSave(attempt.terms).catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not save.'));
      }}>Save draft</Button>
    </div>
  </section>;
}
